// widget/src/hooks/useProactive.ts
//
// Owns proactive-open trigger timing + the anti-annoyance guardrails.
// v1 ships the product-page-dwell trigger only (highest value, lowest
// annoyance). Cart-dwell + returning-visitor are deferred to phase 2.
//
// Guardrails (all enforced here):
//   - max once per session (sessionStorage "pearl-proactive-shown")
//   - dismissing a proactive open suppresses all further opens this
//     session ("pearl-proactive-dismissed")
//   - never fire if the customer already opened/used the chat
//   - never on checkout / account pages
//   - respect prefers-reduced-motion

import { useEffect, useRef } from "react"
import type { PageContext } from "../types"

// Fired-state is now per PRODUCT (keyed by handle), not once per session — so
// each NEW product the customer dwells on gets its own pitch (the whole point
// of product-aware openers). Revisiting the same product won't re-fire, and the
// 12s dwell means rapid browsing never triggers it. DISMISSED is still
// session-wide: minimising the strip stops ALL further openers for the visit.
const SHOWN_PREFIX = "pearl-proactive-shown:"
const DISMISSED = "pearl-proactive-dismissed"
const DWELL_MS = 6000

interface Opts {
  apiBaseUrl: string
  pageContext: PageContext
  active: boolean // is the chat panel open
  hasEngaged: boolean // has the user sent any message this session
  /**
   * Called when the proactive trigger fires.
   *  - `opener` is the curated greeting copy, or null → widget falls back to
   *     buildProactiveFallback(pageContext.productTitle).
   *  - `chips` is the curated quick-reply list, or null → widget falls back
   *     to the URL-pattern classifier (chipsForProduct).
   * Both fields are populated by the /api/chat/opener endpoint from the
   * pearl_product_openers table.
   */
  onFire: (opener: string | null, chips: string[] | null) => void
}

export function useProactive({
  apiBaseUrl,
  pageContext,
  active,
  hasEngaged,
  onFire,
}: Opts): { dismissProactive: () => void } {
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current) return
    if (active || hasEngaged) return
    if (typeof sessionStorage === "undefined") return
    // Session-wide dismiss wins. (Per-product "already shown" is checked once
    // we know the handle, below.)
    if (sessionStorage.getItem(DISMISSED)) return

    const path = pageContext.currentPage ?? ""
    if (/\/(checkout|account)/.test(path)) return
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return

    // v1: product-page dwell only.
    const handle = pageContext.productHandle
    if (!handle) return
    // Already pitched THIS product this session? Don't repeat it.
    if (sessionStorage.getItem(SHOWN_PREFIX + handle)) return

    const timer = window.setTimeout(async () => {
      if (firedRef.current || active || hasEngaged) return
      firedRef.current = true
      try {
        sessionStorage.setItem(SHOWN_PREFIX + handle, "1")
      } catch {
        /* ignore */
      }
      let opener: string | null = null
      let chips: string[] | null = null
      // MASTER GATE — fail CLOSED. Proactive selling is opt-in: we fire only
      // when the server explicitly answers `enabled: true`. A non-OK response,
      // a network error, or an older deploy that doesn't send the field all
      // mean "don't pitch".
      //
      // This used to fail OPEN: a null opener (which the server returns both
      // when the engine is disabled AND when a handle has no curated opener)
      // fell through to onFire(), so the widget popped a generic template
      // opener + product chips on EVERY product page even with
      // messaging_mode_config.proactive_enabled = false. The server gate was
      // already off and simply ignored client-side — that is what "the
      // proactive chips keep showing up" was.
      let proactiveEnabled = false
      try {
        const r = await fetch(
          `${apiBaseUrl}/api/chat/opener?handle=${encodeURIComponent(handle)}`,
        )
        if (r.ok) {
          const body = (await r.json()) as {
            enabled?: boolean
            opener?: string | null
            chips?: string[] | null
          }
          proactiveEnabled = body.enabled === true
          opener = body.opener ?? null
          // Defensive: only accept arrays with at least 2 entries — anything
          // else means the migration isn't applied or the curator left chips
          // unset → let the widget's URL-pattern classifier handle it.
          if (Array.isArray(body.chips) && body.chips.length >= 2) {
            chips = body.chips.filter(
              (c): c is string => typeof c === "string" && c.trim().length > 0,
            )
            if (chips.length < 2) chips = null
          }
        }
      } catch {
        /* stays disabled — see the fail-closed note above */
      }
      if (!proactiveEnabled) return
      onFire(opener, chips)
    }, DWELL_MS)

    return () => window.clearTimeout(timer)
  }, [
    apiBaseUrl,
    pageContext.currentPage,
    pageContext.productHandle,
    active,
    hasEngaged,
    onFire,
  ])

  function dismissProactive() {
    try {
      sessionStorage.setItem(DISMISSED, "1")
    } catch {
      /* ignore */
    }
  }

  return { dismissProactive }
}
